<template>
    <tr class="border-left border-right" :class="{ 'border-top': isManufacturerReturn }">
        <td rowspan="3" class="pl-3" v-if="showDelete">
            <i class="fa fa-lg fa-trash text-danger" style="cursor: pointer;" @click="emitDelete"></i>
        </td>
        <td class="pt-3 pl-3 text-center minWidth">
            <a v-if="returnItem.warranty_image_url" :href="returnItem.warranty_image_url" target="_blank">
                <img :src="returnItem.warranty_image_url" alt="Product Image" style="max-width: 50px; max-height: 50px;" />
            </a>
            <img v-else-if="returnItem.product.thumb_url" :src="returnItem.product.thumb_url" alt="Product Image"
                style="max-width: 50px; max-height: 50px;" />
        </td>
        <td class="pl-3 pr-3 pt-3" colspan="2">
            <div>
                <span class="font-weight-bold">{{ returnItem.product.sku }}
                </span>
                <span v-if="returnItem.product.name" class="text-secondary"> - {{
                    returnItem.product.name }}</span>
            </div>
        </td>
        <td v-if="isAdminView || showOutcome" rowspan="3" class="text-right pl-3 pr-3" :style="outcomeColumnStyle">
            <template v-if="isAdminView && showActionButtons && returnItem.outcome == 'Pending'">
                <div v-if="!approved && !rejected" class="text-nowrap">
                    <div v-if="!approved && returnItem.is_warranty" class="text-nowrap">
                        <SelectButton v-model="returnItem.compensation_method" class="RefundExchangeSelectButton m-2"
                            :options="['Exchange', 'Credit']" @change="compensationMethodChanged" />
                    </div>
                    <div style="white-space: nowrap;">
                        <JetButton v-if="!approved && !rejected && returnItem.quantity > 1" class="m-2" @click="split">
                            <i class="fa-solid fa-right-from-bracket mr-1"></i>Split
                        </JetButton>
                        <JetButton class="btn-success m-2" @click="() => this.approved = true"><i
                                class="fa-solid fa-thumbs-up mr-1"></i>Approve</JetButton>
                        <JetDangerButton class="m-2" @click="reject"><i class="fa-solid fa-thumbs-down mr-1"></i>Reject
                        </JetDangerButton>
                    </div>
                </div>
                <template v-else>
                    <div class="mt-3">
                        <span class="p-float-label pr-2 pl-2 w-100">
                            <Textarea class="w-100" v-model="outcome_comment" autoResize rows="2" />
                            <label class="ml-2">Explain{{ rejected ? ' (Required)' : '' }}</label>
                        </span>
                    </div>
                    <div v-if="rejected" class="text-nowrap">
                        <JetButton class="m-2" @click="resetOutcome"><i class="fa-solid fa-arrow-left"></i>
                        </JetButton>
                        <JetDangerButton class="m-2" @click="recordOutcome"><i
                                class="fa-solid fa-thumbs-down mr-1"></i>Reject
                        </JetDangerButton>
                    </div>
                    <div v-if="approved" class="text-nowrap">
                        <JetButton class="m-2" @click="resetOutcome"><i class="fa-solid fa-arrow-left"></i>
                        </JetButton>
                        <JetButton class="m-2" v-if="!returnItem.is_warranty"
                            @click="recordInventoryAction('Add to Inventory')"><i class="fa-solid fa-cubes mr-1"></i>To
                            Inventory
                        </JetButton>
                        <JetButton class="m-2" v-if="returnItem.is_warranty"
                            @click="recordInventoryAction('Return to Manufacturer')"><i
                                class="fa-solid fa-truck-arrow-right mr-1"></i>To Manufacturer</JetButton>
                        <JetDangerButton class="m-2" @click="recordInventoryAction('Discard')"><i
                                class="fa fa-trash mr-1"></i>Discard</JetDangerButton>
                    </div>
                </template>
            </template>
            <div v-else>
                <div :class="outcomeTextClass" class="text-nowrap">
                    <i class="fa-solid mr-2" :class="outcomeIcon"></i>
                    <span>{{ outcomeText }}</span>
                </div>
                <div v-if="returnItem.compensation_payment" class="text-nowrap mt-2">
                    <a :href="route('mtd.payment.receipt', returnItem.compensation_payment.id)" target="_blank">
                        Payment #{{ returnItem.compensation_payment.id }} {{
                            formatDollars(returnItem.compensation_payment.amount) }}
                    </a>
                </div>
                <div v-if="returnItem.compensation_order_item" class="text-nowrap mt-2">
                    <a :href="route(orderRoute, returnItem.compensation_order_item.order_id)" target="_blank">
                        Order #{{ returnItem.compensation_order_item.order_id }}
                    </a>
                </div>
                <div v-if="returnItem.outcome_comment" class="text-nowrap mt-2">
                    <span class="text-secondary">{{ returnItem.outcome_comment }}</span>
                </div>
            </div>
        </td>
    </tr>
    <tr class="border-left border-right" style="vertical-align:top;">
        <td class="text-center pl-3" style="vertical-align:top;">
            <span class="font-italic">{{ returnItem.is_warranty ? 'Warranty' : 'Return' }}</span>
        </td>
        <td style="vertical-align:top;" colspan="2">
            <div class="pl-3" v-if="returnItem.warranty_serial_number">
                <span class="font-weight-bold">Serial Number: </span>
                <span>{{ returnItem.warranty_serial_number }}</span>
            </div>
            <div class="pl-3" v-if="returnItem.comments">
                <span class="font-weight-bold">Comments: </span>
                <span>{{ returnItem.comments }}</span>
            </div>
        </td>
    </tr>
    <tr class="border-left border-right border-bottom" :class="{ strikethrough: returnItem.rejected }">
        <td></td>
        <td class="text-right pl-3" :class="{ columnFillRemaining: !showOutcome }">
            <span v-if="returnItem.compensation_method == 'Credit' && !isManufacturerReturn" class="text-nowrap">
                <span>{{ returnItem.quantity }} x ({{
                    formatDollars(returnItem.shipment_item?.order_item?.cost_item)
                }}
                    <span v-if="returnItem.restocking_fee_per_item > 0" class="text-danger"> - {{
                        formatDollars(returnItem.restocking_fee_per_item)
                    }}
                        <span style="font-size: .8em; font-style: italic;"> Restocking Fee</span>
                    </span>)
                </span>
            </span>
            <span v-else>Quantity</span>
        </td>
        <td v-if="returnItem.compensation_method == 'Credit' && !isManufacturerReturn" class="pl-3 pr-3 minWidth">
            <span>
                {{ formatDollars(returnItem.total_credit) }}
            </span>
        </td>
        <td v-else class="text-center pl-3 pr-3 minWidth">
            <span>{{ returnItem.quantity }}</span>
        </td>
    </tr>
</template>

<script>
import JetButton from '@/Jetstream/Button'
import JetDangerButton from '@/Jetstream/DangerButton'
import jquery from 'jquery';
import SelectButton from 'primevue/selectbutton'
import Textarea from 'primevue/textarea';

const successValues = ['Add to Inventory', 'Return to Manufacturer', 'Discard'];

export default {
    emits: ['returnItemDelete', 'returnItemChanged'],
    components: {
        JetButton,
        JetDangerButton,
        SelectButton,
        Textarea,
        jquery
    },
    data: {
        approved: false,
        rejected: false,
        outcome: null,
        outcome_comment: null,
    },
    props: {
        returnItem: Object,
        showDelete: {
            type: Boolean,
            default: false,
        },
        isAdminView: {
            type: Boolean,
            default: false,
        },
        showOutcome: {
            type: Boolean,
            default: false,
        },
        showActionButtons: {
            type: Boolean,
            default: false,
        },
        isManufacturerReturn: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        outcomeTextClass() {
            let outcomeTextClass = ''

            if (this.returnItem.outcome == 'Pending') {
                outcomeTextClass = 'text-secondary'
            }
            else if (this.returnItem.outcome == 'Rejected') {
                outcomeTextClass = 'text-danger'
            }
            else if (successValues.includes(this.returnItem.outcome)) {
                outcomeTextClass = 'text-success'
            }
            return outcomeTextClass
        },
        outcomeText() {
            if (!this.isAdminView && successValues.includes(this.returnItem.outcome)) {
                return 'Approved'
            }
            return this.returnItem.outcome
        },
        outcomeIcon() {
            let icon
            switch (this.returnItem.outcome) {
                case 'Rejected':
                    icon = 'fa-thumbs-down'
                    break;
                case 'Add to Inventory':
                    icon = this.isAdminView ? 'fa-cubes' : 'fa-thumbs-up'
                    break;
                case 'Return to Manufacturer':
                    icon = this.isAdminView ? 'fa-truck-arrow-right' : 'fa-thumbs-up'
                    break;
                case 'Discard':
                    icon = this.isAdminView ? 'fa-trash' : 'fa-thumbs-up'
                    break;
                case 'Pending':
                    icon = 'fa-clock'
                    break;
                default:
                    icon = 'fa-question'
                    break;
            }
            return icon
        },
        outcomeColumnStyle() {
            return this.isAdminView ? 'width: 500px;' : ''
        },
        orderRoute() {
            return this.isAdminView ? 'admin.order.view' : 'mtd.order.view'
        },
    },
    methods: {
        emitDelete() {
            this.$emit('returnItemDelete', this.returnItem.id)
        },
        split() {
            axios.post(this.route('admin.returns.splitreturnitem', this.returnItem.id))
                .then((response) => {
                    if (response.data.success) {
                        this.$emit('returnItemChanged');
                    }
                });
        },
        compensationMethodChanged() {
            axios.post(this.route('admin.returns.updatecompensation', this.returnItem.id),
                { compensation_method: this.returnItem.compensation_method });
        },
        resetOutcome() {
            this.approved = false
            this.rejected = false
        },
        reject() {
            this.rejected = true;
            this.outcome = "Rejected"
        },
        recordInventoryAction(outcome) {
            this.outcome = outcome;
            this.recordOutcome();
        },
        recordOutcome() {
            if (this.outcome == 'Rejected' && !this.outcome_comment) {
                toastr.error("Please explain why you are rejecting this item.  This will be visible to the customer.");
            }
            else {
                axios.post(this.route('admin.returns.returnitem.recordaction', this.returnItem.id),
                    { outcome: this.outcome, outcome_comment: this.outcome_comment })
                    .then((response) => {
                        if (response.data.success) {
                            this.$emit('returnItemChanged');
                        }
                    });
            }
        },
    },
}
</script>

<style>
.RefundExchangeSelectButton>.p-button {
    width: 80px;
}

.columnFillRemaining {
    width: 99%;
}

td.minWidth {
    width: 1%;
    white-space: nowrap;
}
</style>